import React, { useState } from 'react';
import { Box, VStack, Text, Select, Button, Textarea, useToast, Checkbox, SimpleGrid, Heading, Flex } from '@chakra-ui/react';
import axios from 'axios';
import CloudArchitectureDiagram from './CloudArchitectureDiagram';

export default function IACDashboard() {
  const [provider, setProvider] = useState('aws');
  const [selectedResources, setSelectedResources] = useState([]);

  const handleProviderChange = (newProvider) => {
    setProvider(newProvider);
    setSelectedResources([]);
    setDiagramData(null);
    setDiagramImage(null);
    setScript('');
  };
  const [script, setScript] = useState('');
  const [diagramData, setDiagramData] = useState(null);
  const [diagramImage, setDiagramImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [diagramLoading, setDiagramLoading] = useState(false);
  const toast = useToast();



  const awsResources = {
    'Compute': ['EC2 Instances (Virtual servers)', 'Auto Scaling Groups', 'Elastic Load Balancers (ALB/NLB)', 'Lambda Functions (Serverless compute)', 'ECS Clusters & Tasks (Containers)', 'EKS Clusters (Kubernetes)'],
    'Storage': ['S3 Buckets (Object storage)', 'EBS Volumes (Block storage)', 'EFS File Systems (Shared file storage)', 'Glacier Vaults (Archival storage)'],
    'Networking': ['VPCs (Virtual Private Cloud)', 'Subnets', 'Route Tables', 'Internet Gateways', 'NAT Gateways', 'Security Groups', 'Network ACLs', 'Elastic IPs'],
    'Database': ['RDS Instances (Relational DB)', 'Aurora Clusters', 'DynamoDB Tables (NoSQL)', 'ElastiCache Clusters', 'Redshift Clusters'],
    'Identity & Access': ['IAM Users', 'IAM Roles', 'IAM Policies', 'Access Keys'],
    'Monitoring & Management': ['CloudWatch Alarms', 'CloudWatch Logs', 'CloudTrail Trails', 'AWS Config Rules'],
    'Security': ['KMS Keys', 'Secrets Manager Secrets', 'Certificate Manager Certificates'],
    'Developer Tools': ['CodePipeline', 'CodeBuild', 'CodeDeploy', 'CodeCommit'],
    'AI/ML': ['SageMaker Models', 'Bedrock Models', 'Comprehend, Rekognition resources']
  };

  const azureResources = {
    'Compute': ['Virtual Machines', 'VM Scale Sets', 'Azure Kubernetes Service (AKS)', 'App Services', 'Azure Functions'],
    'Storage': ['Blob Storage', 'File Storage', 'Disk Storage', 'Archive Storage'],
    'Networking': ['Virtual Networks', 'Subnets', 'Load Balancers', 'Application Gateways', 'ExpressRoute'],
    'Database': ['Azure SQL Database', 'Cosmos DB', 'PostgreSQL/MySQL/MariaDB', 'Azure Synapse Analytics'],
    'Identity & Security': ['Azure Active Directory (Entra ID)', 'Key Vault', 'Defender for Cloud', 'Azure Policy'],
    'Monitoring & Management': ['Azure Monitor', 'Log Analytics', 'Azure Advisor', 'Resource Graph'],
    'Developer Tools': ['Azure DevOps', 'GitHub Actions', 'Visual Studio Integration'],
    'AI/ML': ['Azure Machine Learning', 'Cognitive Services', 'Bot Services']
  };

  const gcpResources = {
    'Compute': ['Compute Engine', 'Google Kubernetes Engine (GKE)', 'Cloud Functions', 'Cloud Run'],
    'Storage': ['Cloud Storage', 'Filestore', 'Persistent Disks', 'Archive Storage'],
    'Networking': ['VPC Networks', 'Load Balancers', 'Cloud NAT', 'Cloud CDN'],
    'Database': ['Cloud SQL', 'BigQuery', 'Firestore', 'Cloud Spanner'],
    'Identity & Security': ['Cloud IAM', 'Secret Manager', 'Cloud KMS', 'Security Command Center'],
    'Monitoring & Management': ['Cloud Monitoring', 'Cloud Logging', 'Cloud Trace', 'Cloud Profiler'],
    'Developer Tools': ['Cloud Build', 'Artifact Registry', 'Cloud Source Repositories'],
    'AI/ML': ['Vertex AI', 'TensorFlow', 'AutoML', 'Vision/Speech APIs']
  };

  const oracleResources = {
    'Compute': ['VM Instances', 'Bare Metal Instances', 'Container Instances', 'GPU Shapes'],
    'Storage': ['Object Storage', 'Block Volumes', 'File Storage', 'Archive Storage'],
    'Networking': ['Virtual Cloud Networks (VCNs)', 'Subnets', 'Internet/NAT Gateways', 'Load Balancers'],
    'Database': ['Autonomous Database', 'Oracle DB on VM/Bare Metal', 'MySQL Database Service', 'Exadata Cloud Service'],
    'Identity & Security': ['Identity Domains', 'IAM Policies', 'Vault', 'Cloud Guard'],
    'Monitoring & Management': ['OCI Monitoring', 'Logging', 'Resource Manager', 'Notifications'],
    'Developer Tools': ['DevOps Pipelines', 'Resource Manager (Terraform)', 'API Gateway'],
    'AI/ML': ['Data Science', 'Vision, Speech, Language Services', 'AI Services']
  };

  const handleResourceToggle = (resource) => {
    setSelectedResources(prev => prev.includes(resource) ? prev.filter(r => r !== resource) : [...prev, resource]);
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post('http://localhost:5003/api/iac/generate', { provider, resources: selectedResources });
      setScript(data.script);
      toast({ title: 'Terraform code generated!', status: 'success', duration: 3000 });
    } catch (error) {
      toast({ title: 'Failed to generate terraform code', status: 'error', duration: 3000 });
    }
    setLoading(false);
  };

  const handleGenerateDiagram = async () => {
    setDiagramLoading(true);
    try {
      const { data } = await axios.post('http://localhost:5003/api/iac/generate-diagram', { provider, resources: selectedResources });
      setDiagramData(data);
      setDiagramImage(null);
      toast({ title: 'Architecture diagram generated!', status: 'success', duration: 3000 });
    } catch (error) {
      toast({ title: 'Failed to generate diagram', status: 'error', duration: 3000 });
    }
    setDiagramLoading(false);
  };

  return (
    <Flex justify="space-between" align="flex-start" p="20px" gap="16px" h="calc(100vh - 120px)" w="100%" overflow="hidden">
      <Box w="23%" minW="240px" maxW="400px" h="100%" bg="rgba(255,255,255,0.1)" p={4} rounded="md" shadow="sm" backdropFilter="blur(10px)" overflowY="auto">
        <VStack spacing={4} align="stretch" h="100%">
          <Box>
            <Text fontWeight="bold" mb={2} color="white">Select Provider</Text>
            <Select value={provider} onChange={(e) => handleProviderChange(e.target.value)} bg="white">
              <option value="aws">AWS</option>
              <option value="azure">Azure</option>
              <option value="gcp">GCP</option>
              <option value="oracle">Oracle</option>
              <option value="cloudformation">CloudFormation</option>
              <option value="ansible">Ansible</option>
            </Select>
          </Box>
          {(provider === 'aws' || provider === 'azure' || provider === 'gcp' || provider === 'oracle') && (
            <Box flex={1} overflowY="auto">
              <Text fontWeight="bold" mb={4} color="white">Select Resources</Text>
              <VStack align="stretch" spacing={4}>
                {Object.entries(provider === 'aws' ? awsResources : provider === 'azure' ? azureResources : provider === 'gcp' ? gcpResources : oracleResources).map(([category, resources]) => (
                  <Box key={category}>
                    <Heading size="sm" mb={2} color="blue.300">{category}</Heading>
                    <SimpleGrid columns={1} spacing={2}>
                      {resources.map(resource => (
                        <Checkbox key={resource} isChecked={selectedResources.includes(resource)} onChange={() => handleResourceToggle(resource)} color="white">{resource}</Checkbox>
                      ))}
                    </SimpleGrid>
                  </Box>
                ))}
              </VStack>
            </Box>
          )}
          <Button colorScheme="blue" onClick={handleGenerate} isLoading={loading} isDisabled={(provider === 'aws' || provider === 'azure' || provider === 'gcp' || provider === 'oracle') && selectedResources.length === 0}>Generate Terraform Code</Button>
          <Button colorScheme="purple" onClick={handleGenerateDiagram} isLoading={diagramLoading} isDisabled={(provider === 'aws' || provider === 'azure' || provider === 'gcp' || provider === 'oracle') && selectedResources.length === 0}>Generate Architecture</Button>
        </VStack>
      </Box>

      <Box flex={1} minW="450px" h="100%" bg="rgba(255,255,255,0.1)" p={4} rounded="md" shadow="sm" backdropFilter="blur(10px)" overflow="hidden">
        <Text fontWeight="bold" mb={4} color="white">Architecture Diagram</Text>
        {diagramData?.nodes?.length > 0 ? (
          <Box h="calc(100% - 50px)">
            <CloudArchitectureDiagram diagramData={diagramData} provider={provider} />
          </Box>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" h="calc(100% - 50px)" bg="rgba(0,0,0,0.2)" rounded="md">
            <Text color="gray.300">Diagram will appear here based on selected resources</Text>
          </Box>
        )}
      </Box>

      <Box w="32%" minW="320px" maxW="500px" h="100%" bg="rgba(255,255,255,0.1)" p={4} rounded="md" shadow="sm" backdropFilter="blur(10px)" overflowY="auto">
        <Text fontWeight="bold" mb={4} color="white">Generated Terraform Code</Text>
        {script ? (
          <Textarea value={script} h="calc(100% - 50px)" fontFamily="monospace" fontSize="sm" bg="rgba(0,0,0,0.3)" color="white" border="none" />
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" h="calc(100% - 50px)" bg="rgba(0,0,0,0.2)" rounded="md">
            <Text color="gray.300">Generated code will appear here</Text>
          </Box>
        )}
      </Box>
    </Flex>
  );
}
